/**
 * Position FAQ parsing / serialization utilities.
 *
 * Candidate-facing Q&A pairs (`Q. ... / A. ...`) are stored in the
 * `frequently_asked_questions` column.
 *
 * Additional questions for the AI agent are stored separately in the
 * `additional_questions` column (newline-separated).
 */

export type faq = {
  q: string;
  a: string;
};

/** Persisted when the user explicitly opts out of candidate FAQ in setup (must match server retell normalization). */
export const POSITION_FAQ_OPT_OUT_SENTINEL = "__POSITION_FAQ_NONE__";

export const isPositionFaqOptOut = (
  raw: string | null | undefined,
): boolean => {
  return (raw ?? "").trim() === POSITION_FAQ_OPT_OUT_SENTINEL;
};

export const parseFaqs = (faqs: string) => {
  const parsedFaqs: faq[] = [];

  let faq: { q: string; a: string } | null = null;
  for (const line of faqs.split(`\n`)) {
    if (line.startsWith("Q.")) {
      if (faq !== null) {
        parsedFaqs.push(faq);
      }
      faq = {
        q: line.slice(3),
        a: "",
      };
    } else if (!line.startsWith("A.") && !!faq?.q && !faq?.a) {
      faq = {
        q: `${faq.q}\n${line}`,
        a: faq.a,
      };
    } else if (line.startsWith("A.") && faq) {
      faq = {
        q: faq.q,
        a: line.slice(3),
      };
    } else if (faq) {
      faq = {
        q: faq.q,
        a: `${faq.a}\n${line}`,
      };
    }
  }
  if (faq) {
    parsedFaqs.push(faq);
  }

  return parsedFaqs;
};

export const formatFaqs = (faqs: faq[]) =>
  faqs
    .filter(({ q, a }) => q.length > 0 || a.length > 0)
    .map(({ q, a }) => `Q. ${q}\nA. ${a}`)
    .join(`\n`);
